import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { OfficialIdentificationCard, type OfficialIdentificationState } from '../components/OfficialIdentificationCard';
import { useRuntimeCapabilities } from '../hooks/useRuntimeCapabilities';
import { API_BASE_URL, buildApiUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type MessageContent =
  | { text: string }
  | { url: string; caption?: string }
  | { url: string; fileName?: string }
  | { sequence: Array<{ type: string; content: any }> };

interface Campaign {
  id: string;
  nome: string;
  targetTags: string[];
  sessionNames: string[];
  sessionName: string;
  messageType: string;
  messageContent: MessageContent;
  randomDelay: number;
  startImmediately: boolean;
  scheduledFor: string | null;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  criadoEm: string;
  session: {
    name: string;
    displayName?: string;
    status: string;
    mePushName: string | null;
  };
  _count: {
    messages: number;
  };
}

interface WhatsAppSession {
  name: string; // Nome real usado na API
  displayName?: string; // Nome exibido ao usuário
  mePushName: string | null;
  meId: string | null;
}

interface ContactTag {
  id: string;
  nome: string;
}

interface SavedTextTemplate {
  id: string;
  title: string;
  body: string;
  createdAt: string;
}

const SAVED_TEXTS_STORAGE_KEY_LEGACY = 'campaign-saved-texts-v1';
const SAVED_TEXTS_STORAGE_KEY_PREFIX = 'campaign-saved-texts-v2';
const REPORT_CERTIFICATION_NOTICE_SEEN_KEY = 'campaign-report-certification-notice-seen-v1';
const REPORT_CERTIFICATION_NOTICE_HIDDEN_KEY = 'campaign-report-certification-notice-hidden-v1';
const OFFICIAL_IDENTIFICATION_NOTICE_HIDDEN_KEY = 'campaign-official-identification-notice-hidden-v1';

const safeStorageSegment = (value?: string | null) => {
  if (!value) return 'none';
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
};

const decodeTokenPayload = (token: string) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};
const DEFAULT_SAVED_TEXTS: SavedTextTemplate[] = [
  {
    id: 'default-contato-inicial',
    title: 'Modelo 1 – Modelo de Contato Inicial',
    body: `Bom dia!

Neste número falo com {{nome}}?

Sou oficial de justiça (documento abaixo) e me encontro com um mandado de Citação/Intimação destinado a(o) senhor(a).

A legislação nos autoriza realizar esse ato por meio virtual (WhatsApp), porém, preciso de uma foto de documento pessoal, caso concorde em receber por aqui.

Não havendo concordância, o mandado será cumprido presencialmente no(s) endereço(s) do mandado.

Obs.: Após a identificação da identidade de {{nome}} enviarei o mandado em até uma hora..`,
    createdAt: new Date(0).toISOString()
  },
  {
    id: 'default-reenvio-sem-resposta',
    title: 'Modelo 2 – Reenvio por não resposta',
    body: `Bom dia!

Conforme me identifiquei anteriormente, sou Oficial de Justiça, meu nome é XXXX - matrícula xxxx - e me encontro com um mandado de *intimação/citação* destinado a {{nome}}.

*Neste número falo ou não com essa pessoa?*

Destinatário: {{nome}}.`,
    createdAt: new Date(0).toISOString()
  }
];

export function CampaignsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { capabilities } = useRuntimeCapabilities();
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [currentReportCampaignId, setCurrentReportCampaignId] = useState<string | null>(null);
  const [showCertificationNotice, setShowCertificationNotice] = useState(false);
  const [hideCertificationNoticeForever, setHideCertificationNoticeForever] = useState(false);
  const [showOfficialIdentificationNotice, setShowOfficialIdentificationNotice] = useState(false);
  const [hideOfficialIdentificationNoticeForever, setHideOfficialIdentificationNoticeForever] = useState(false);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportItemsPerPage] = useState(8);
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [whatsappSessions, setWhatsappSessions] = useState<WhatsAppSession[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
  const [fileInfos, setFileInfos] = useState<Record<string, { name: string, size: number, type: string }>>({});
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [mainTab, setMainTab] = useState<'acoes' | 'textos'>('acoes');
  const [savedTexts, setSavedTexts] = useState<SavedTextTemplate[]>([]);
  const [showSavedTextModal, setShowSavedTextModal] = useState(false);
  const [savedTextTitle, setSavedTextTitle] = useState('');
  const [savedTextBody, setSavedTextBody] = useState('');
  const [editingSavedTextId, setEditingSavedTextId] = useState<string | null>(null);
  const [selectedSavedTextByMessage, setSelectedSavedTextByMessage] = useState<Record<number, string>>({});
  const [savedTextsLoaded, setSavedTextsLoaded] = useState(false);
  const [officialIdentification, setOfficialIdentification] = useState<OfficialIdentificationState>({
    hasDocument: false,
    documentUrl: null,
    isImage: false
  });
  const [searchTerm] = useState('');
  const [statusFilter] = useState<'all' | 'waiting' | 'completed' | 'cancelled'>('all');

  const savedTextsStorageKey = useMemo(() => {
    const token = localStorage.getItem('auth_token');
    const tokenPayload = token ? decodeTokenPayload(token) : null;
    const userId = user?.id || tokenPayload?.id || tokenPayload?.userId || tokenPayload?.sub || 'anonymous';
    const tenantId = user?.tenantId || tokenPayload?.tenantId || localStorage.getItem('selected_tenant_id') || 'none';
    return `${SAVED_TEXTS_STORAGE_KEY_PREFIX}:${safeStorageSegment(tenantId)}:${safeStorageSegment(userId)}`;
  }, [user?.id, user?.tenantId]);

  const reportTotals = reportData?.stats || {};
  const reportTotal = Number(reportTotals.total ?? 0);
  const reportSent = Number(reportTotals.sent ?? 0);
  const reportFailed = Number(reportTotals.failed ?? 0);
  const reportPending = Number(
    reportTotals.pending ?? Math.max(reportTotal - reportSent - reportFailed, 0)
  );
  const reportProgress = reportTotal > 0
    ? Math.min(100, Math.round(((reportSent + reportFailed) / reportTotal) * 100))
    : 0;
  const sortedSavedTexts = [...savedTexts].sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
  const reportPendingMessages = (reportData?.campaign?.messages || []).filter(
    (message: any) => message.status !== 'SENT' && message.status !== 'FAILED'
  );
  const campaignReportPollInterval = capabilities.recommendations.campaignsPollingIntervalMs;

  const getDashboardStatus = (status: string) => {
    if (['PENDING', 'RUNNING', 'PAUSED'].includes(status)) {
      return { label: 'Aguardando', tone: 'waiting' as const };
    }

    if (status === 'COMPLETED') {
      return { label: 'Concluído', tone: 'completed' as const };
    }

    return { label: 'Cancelado', tone: 'cancelled' as const };
  };

  const getDashboardStatusStyles = (tone: 'waiting' | 'completed' | 'cancelled') => {
    if (tone === 'waiting') {
      return 'bg-amber-100 text-amber-800 ring-1 ring-amber-200';
    }

    if (tone === 'completed') {
      return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200';
    }

    return 'bg-rose-100 text-rose-800 ring-1 ring-rose-200';
  };

  const getDashboardProgressClass = (tone: 'waiting' | 'completed' | 'cancelled') => {
    if (tone === 'waiting') return 'bg-amber-500';
    if (tone === 'completed') return 'bg-emerald-500';
    return 'bg-rose-500';
  };

  const filteredCampaigns = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return campaigns.filter((campaign) => {
      const { tone } = getDashboardStatus(campaign.status);
      const matchesStatus = statusFilter === 'all' || tone === statusFilter;
      const matchesSearch =
        !query ||
        campaign.nome.toLowerCase().includes(query) ||
        (campaign.createdByName || '').toLowerCase().includes(query) ||
        (campaign.sessionName || '').toLowerCase().includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [campaigns, searchTerm, statusFilter]);

  const dashboardStats = useMemo(() => {
    return campaigns.reduce(
      (acc, campaign) => {
        const { tone } = getDashboardStatus(campaign.status);
        acc.total += 1;
        acc[tone] += 1;
        return acc;
      },
      { total: 0, waiting: 0, completed: 0, cancelled: 0 }
    );
  }, [campaigns]);

  // Form states
  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    sessionNames: [] as string[],
    sessionName: '',
    messageType: 'sequence',
    messageContent: { sequence: [] as Array<{ type: string; content: any }> } as MessageContent,
    randomDelay: 40,
    startImmediately: true,
    scheduledFor: ''
  });
  const hasSelectedSession = formData.sessionNames.length > 0;

  useEffect(() => {
    loadCampaigns();
    loadContactTags();
    loadWhatsAppSessions();
  }, []);

  useEffect(() => {
    if (!showCreateModal) return;
    if (hasSelectedSession) return;
    if (whatsappSessions.length === 0) return;

    setFormData(prev => ({
      ...prev,
      sessionNames: [whatsappSessions[0].name]
    }));
  }, [showCreateModal, hasSelectedSession, whatsappSessions]);

  useEffect(() => {
    if (!showCreateModal) {
      setShowOfficialIdentificationNotice(false);
      return;
    }

    const hideNoticePermanently = localStorage.getItem(OFFICIAL_IDENTIFICATION_NOTICE_HIDDEN_KEY) === 'true';
    if (hideNoticePermanently) {
      return;
    }

    setHideOfficialIdentificationNoticeForever(false);
    setShowOfficialIdentificationNotice(true);
  }, [showCreateModal]);

  useEffect(() => {
    setSavedTextsLoaded(false);
    try {
      const raw = localStorage.getItem(savedTextsStorageKey);
      const legacyRaw = localStorage.getItem(SAVED_TEXTS_STORAGE_KEY_LEGACY);
      const sourceRaw = raw ?? legacyRaw;

      if (!sourceRaw) {
        setSavedTexts(DEFAULT_SAVED_TEXTS);
        setSavedTextsLoaded(true);
        return;
      }

      const parsed = JSON.parse(sourceRaw);
      if (Array.isArray(parsed)) {
        setSavedTexts(parsed.length > 0 ? parsed : DEFAULT_SAVED_TEXTS);
      } else {
        setSavedTexts(DEFAULT_SAVED_TEXTS);
      }

      if (!raw && legacyRaw) {
        localStorage.setItem(savedTextsStorageKey, sourceRaw);
      }
    } catch (error) {
      console.error('Erro ao carregar textos salvos:', error);
      setSavedTexts(DEFAULT_SAVED_TEXTS);
    } finally {
      setSavedTextsLoaded(true);
    }
  }, [savedTextsStorageKey]);

  useEffect(() => {
    if (!savedTextsLoaded) return;
    try {
      localStorage.setItem(savedTextsStorageKey, JSON.stringify(savedTexts));
    } catch (error) {
      console.error('Erro ao salvar textos salvos:', error);
    }
  }, [savedTexts, savedTextsLoaded, savedTextsStorageKey]);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const resolvedUrl =
      /^https?:\/\//i.test(url) || url.startsWith(API_BASE_URL)
        ? url
        : buildApiUrl(url);

    return fetch(resolvedUrl, {
      ...options,
      headers,
    });
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/campaigns');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = data.campaigns || [];
      setCampaigns(list);
      return list;
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadContactTags = async () => {
    try {
      const response = await authenticatedFetch('/campaigns/tags');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const tags = await response.json();
      setContactTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadWhatsAppSessions = async () => {
    try {
      const response = await authenticatedFetch('/campaigns/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const sessions = await response.json();
      setWhatsappSessions(sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag)
        ? prev.targetTags.filter(t => t !== tag)
        : [...prev.targetTags, tag]
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (creatingCampaign) return;

    if (formData.targetTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria de contatos');
      return;
    }

    if (formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }

    if (!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem à campanha');
      return;
    }

    try {
      setCreatingCampaign(true);
      const campaignName = formData.nome;
      const sequence = formData.messageContent.sequence;
      const isSequence = sequence.length > 1;

      let finalMessageType: string;
      let finalMessageContent: any;

      if (isSequence) {
        finalMessageType = 'sequence';
        finalMessageContent = { sequence };
      } else {
        const singleMessage = sequence[0];
        finalMessageType = singleMessage.type;
        finalMessageContent = singleMessage.content;
      }

      const campaignData = {
        ...formData,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        scheduledFor: formData.startImmediately ? null : formData.scheduledFor || null
      };

      const response = await authenticatedFetch('/campaigns', {
        method: 'POST',
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map((err: any) => err.msg).join(', ');
          throw new Error(validationErrors);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      let createdCampaignId: string | null = null;
      try {
        const responseData = await response.json();
        const createdCampaign = responseData?.campaign ?? responseData;
        createdCampaignId = createdCampaign?.id ?? null;
      } catch (parseError) {
        createdCampaignId = null;
      }

      toast.success('Campanha criada com sucesso!');
      setShowCreateModal(false);
      resetForm();
      const campaignsList = await loadCampaigns();

      if (createdCampaignId) {
        handleViewReport(createdCampaignId);
      } else if (campaignsList.length > 0) {
        const candidates = campaignsList.filter((campaign: Campaign) => campaign.nome === campaignName);
        const sorted = (candidates.length ? candidates : campaignsList).sort(
          (a: Campaign, b: Campaign) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
        );
        handleViewReport(sorted[0].id);
      }
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar campanha');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      targetTags: [],
      sessionNames: [],
      sessionName: '',
      messageType: 'sequence',
      messageContent: { sequence: [] },
      randomDelay: 30,
      startImmediately: true,
      scheduledFor: ''
    });
    setUploadingFiles({});
    setFileInfos({});
    setSelectedSavedTextByMessage({});
  };

  const saveTextTemplate = (title: string, body: string) => {
    const cleanedBody = body.trim();
    if (!cleanedBody) {
      toast.error('Digite um texto para salvar');
      return false;
    }

    const nextTitle = title.trim() || `Texto ${savedTexts.length + 1}`;

    if (editingSavedTextId) {
      setSavedTexts(prev => prev.map(template =>
        template.id === editingSavedTextId
          ? {
              ...template,
              title: nextTitle,
              body: cleanedBody
            }
          : template
      ));
      toast.success('Texto atualizado com sucesso');
      return true;
    }

    const newTemplate: SavedTextTemplate = {
      id: Date.now().toString(),
      title: nextTitle,
      body: cleanedBody,
      createdAt: new Date().toISOString()
    };

    setSavedTexts(prev => [newTemplate, ...prev]);
    toast.success('Texto salvo com sucesso');
    return true;
  };

  const handleCreateSavedText = () => {
    const saved = saveTextTemplate(savedTextTitle, savedTextBody);
    if (!saved) return;
    setSavedTextTitle('');
    setSavedTextBody('');
    setEditingSavedTextId(null);
    setShowSavedTextModal(false);
  };

  const handleEditSavedText = (template: SavedTextTemplate) => {
    setSavedTextTitle(template.title);
    setSavedTextBody(template.body);
    setEditingSavedTextId(template.id);
    setShowSavedTextModal(true);
  };

  const handleCancelEditSavedText = () => {
    setSavedTextTitle('');
    setSavedTextBody('');
    setEditingSavedTextId(null);
    setShowSavedTextModal(false);
  };

  const applySavedTextToMessage = (template: SavedTextTemplate, targetIndex: number) => {
    setFormData(prev => {
      const sequence = ('sequence' in prev.messageContent) ? prev.messageContent.sequence : [];
      const nextSequence = sequence.map((seqItem, i) => {
        if (i !== targetIndex || seqItem.type !== 'text') return seqItem;

        if (seqItem.content?.useVariations) {
          const currentVariations = Array.isArray(seqItem.content.variations)
            ? [...seqItem.content.variations]
            : [''];
          const targetVariationIndex = currentVariations.findIndex((variation: string) => !variation?.trim());
          const resolvedIndex = targetVariationIndex === -1 ? 0 : targetVariationIndex;
          currentVariations[resolvedIndex] = `${currentVariations[resolvedIndex] || ''}${template.body}`;

          return {
            ...seqItem,
            content: {
              ...seqItem.content,
              variations: currentVariations
            }
          };
        }

        const currentText = seqItem.content?.text || '';
        return {
          ...seqItem,
          content: {
            ...seqItem.content,
            text: `${currentText}${template.body}`
          }
        };
      });

      return {
        ...prev,
        messageContent: { sequence: nextSequence }
      };
    });

    toast.success('Texto aplicado à mensagem');
  };

  const handleFileUpload = async (file: File, messageIndex: number, variationIndex?: number) => {
    const uploadKey = variationIndex !== undefined ? `${messageIndex}-${variationIndex}` : messageIndex.toString();
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (selectedTenantId) {
        headers['X-Tenant-Id'] = selectedTenantId;
      }

      const response = await fetch(buildApiUrl('/media/upload'), {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      // Armazenar informações do arquivo
      if (variationIndex !== undefined) {
        setFileInfos(prev => ({
          ...prev,
          [`${messageIndex}-${variationIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      } else {
        setFileInfos(prev => ({
          ...prev,
          [messageIndex]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      }

      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];

      if (variationIndex !== undefined) {
        // Upload para variação
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i === messageIndex) {
            const mediaVariations = [...(seqItem.content.mediaVariations || [])];
            // Garantir que o array tenha o tamanho necessário
            while (mediaVariations.length <= variationIndex) {
              mediaVariations.push({ url: '', caption: '', fileName: '' });
            }
            mediaVariations[variationIndex] = {
              ...mediaVariations[variationIndex],
              url: data.fileUrl,
              fileName: data.originalName
            };
            return {
              ...seqItem,
              content: { ...seqItem.content, mediaVariations }
            };
          }
          return seqItem;
        });

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      } else {
        // Upload para modo single
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex ? {
            ...seqItem,
            content: {
              ...seqItem.content,
              url: data.fileUrl,
              fileName: data.originalName
            }
          } : seqItem
        );

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleRemoveFile = (messageIndex: number) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex ? {
        ...seqItem,
        content: { ...seqItem.content, url: '' }
      } : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));

    setFileInfos(prev => {
      const updated = { ...prev };
      delete updated[messageIndex];
      return updated;
    });

    toast.success('Arquivo removido');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const looksLikeImageUrl = (url?: string | null) => {
    if (!url) return false;
    return /\.(png|jpe?g|webp|gif)(\?|#|$)/i.test(url);
  };

  const copyTextToClipboard = async (text: string, successMessage = 'Link copiado') => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      toast.success(successMessage);
    } catch (error) {
      console.error('Erro ao copiar texto:', error);
      toast.error('Não foi possível copiar o link');
    }
  };

  const appendMediaLinkToCaption = (messageIndex: number, mediaUrl: string, variationIndex?: number) => {
    if (!mediaUrl) {
      toast.error('Nenhum link disponível para inserir');
      return;
    }

    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const currentMessage = currentSequence[messageIndex];

    if (!currentMessage) {
      toast.error('Mensagem não encontrada');
      return;
    }

    if (variationIndex !== undefined) {
      const currentVariation = currentMessage.content?.mediaVariations?.[variationIndex];
      const currentCaption = currentVariation?.caption || '';

      if (currentCaption.includes(mediaUrl)) {
        toast('O link da mídia já está na legenda');
        return;
      }

      const nextCaption = currentCaption.trim() ? `${currentCaption}\n${mediaUrl}` : mediaUrl;
      const newSequence = currentSequence.map((seqItem, i) => {
        if (i !== messageIndex) return seqItem;

        const mediaVariations = [...(seqItem.content.mediaVariations || [])];
        while (mediaVariations.length <= variationIndex) {
          mediaVariations.push({ url: '', caption: '', fileName: '' });
        }

        mediaVariations[variationIndex] = {
          ...mediaVariations[variationIndex],
          caption: nextCaption,
        };

        return {
          ...seqItem,
          content: { ...seqItem.content, mediaVariations }
        };
      });

      setFormData(prev => ({
        ...prev,
        messageContent: { sequence: newSequence }
      }));

      toast.success('Link da mídia inserido na legenda');
      return;
    }

    const currentCaption = currentMessage.content?.caption || '';
    if (currentCaption.includes(mediaUrl)) {
      toast('O link da mídia já está na legenda');
      return;
    }

    const nextCaption = currentCaption.trim() ? `${currentCaption}\n${mediaUrl}` : mediaUrl;
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex
        ? {
            ...seqItem,
            content: { ...seqItem.content, caption: nextCaption }
          }
        : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));

    toast.success('Link da mídia inserido na legenda');
  };

  const applyOfficialIdentificationToMessage = async (messageIndex: number, variationIndex?: number) => {
    try {
      let identificationData = officialIdentification;

      if (!identificationData.documentUrl) {
        const response = await authenticatedFetch('/auth/oficial-identificacao');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        identificationData = {
          hasDocument: Boolean(data?.data?.documentUrl),
          documentUrl: data?.data?.documentUrl || null,
          isImage: Boolean(data?.data?.isImage) || looksLikeImageUrl(data?.data?.documentUrl)
        };
        setOfficialIdentification(identificationData);
      }

      const documentUrl = identificationData.documentUrl;
      const isImage = identificationData.isImage || looksLikeImageUrl(documentUrl);

      if (!documentUrl) {
        toast.error('Nenhum documento de identificação do oficial foi enviado');
        return;
      }

      if (!isImage) {
        toast.error('A identificação do oficial precisa estar em formato de imagem para uso aqui');
        return;
      }

      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];

      if (variationIndex !== undefined) {
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i !== messageIndex) return seqItem;

          const mediaVariations = [...(seqItem.content.mediaVariations || [])];
          while (mediaVariations.length <= variationIndex) {
            mediaVariations.push({ url: '', caption: '', fileName: '' });
          }

          mediaVariations[variationIndex] = {
            ...mediaVariations[variationIndex],
            url: documentUrl,
            fileName: 'Identificação do Oficial de Justiça'
          };

          return {
            ...seqItem,
            content: { ...seqItem.content, mediaVariations }
          };
        });

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));

        setFileInfos(prev => ({
          ...prev,
          [`${messageIndex}-${variationIndex}`]: {
            name: 'Identificação do Oficial de Justiça',
            size: 0,
            type: 'image/*'
          }
        }));
      } else {
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex
            ? {
                ...seqItem,
                content: {
                  ...seqItem.content,
                  url: documentUrl,
                  fileName: 'Identificação do Oficial de Justiça'
                }
              }
            : seqItem
        );

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));

        setFileInfos(prev => ({
          ...prev,
          [messageIndex]: {
            name: 'Identificação do Oficial de Justiça',
            size: 0,
            type: 'image/*'
          }
        }));
      }

      toast.success('Identificação do oficial carregada na mensagem');
    } catch (error) {
      console.error('Erro ao carregar identificação do oficial na mensagem:', error);
      toast.error('Não foi possível carregar a identificação do oficial');
    }
  };

  const handleToggleCampaign = async (campaignId: string, action: 'pause' | 'resume') => {
    try {
      const response = await authenticatedFetch(`/campaigns/${campaignId}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ action })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success(`Campanha ${action === 'pause' ? 'pausada' : 'retomada'} com sucesso!`);
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao alterar status da campanha:', error);
      toast.error('Erro ao alterar status da campanha');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta lista?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/campaigns/${campaignId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (currentReportCampaignId === campaignId) {
        setShowReportModal(false);
        setCurrentReportCampaignId(null);
        setReportData(null);
        setReportCurrentPage(1);
      }

      setCampaigns((current) => current.filter((campaign) => campaign.id !== campaignId));

      toast.success('Lista excluída com sucesso!');
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao excluir lista:', error);
      toast.error('Erro ao excluir lista');
    }
  };

  const handleReenviarCampaign = async (campaignId: string) => {
    try {
      const data = await loadCampaignReport(campaignId);
      const campaign = data?.campaign;

      if (!campaign) {
        throw new Error('Campanha não encontrada');
      }

      setMainTab('acoes');
      setShowCreateModal(true);
      setFormData({
        nome: `${campaign.nome} - Reenvio`,
        targetTags: Array.isArray(campaign.targetTags) ? campaign.targetTags : [],
        sessionNames: Array.isArray(campaign.sessionNames) ? campaign.sessionNames : [],
        sessionName: Array.isArray(campaign.sessionNames) && campaign.sessionNames.length > 0 ? campaign.sessionNames[0] : (campaign.sessionName || ''),
        messageType: campaign.messageType || 'sequence',
        messageContent: campaign.messageContent || { sequence: [] },
        randomDelay: Number.isFinite(campaign.randomDelay) ? campaign.randomDelay : 40,
        startImmediately: true,
        scheduledFor: ''
      });
    } catch (error) {
      console.error('Erro ao preparar reenvio da lista:', error);
      toast.error('Erro ao preparar reenvio da lista');
    }
  };

  const handleOpenContactsList = (campaignId: string) => {
    navigate(`/campanhas/${campaignId}/lista-contatos`);
  };

  const handleCancelCampaign = async (campaignId: string) => {
    if (!confirm('Tem certeza que deseja cancelar definitivamente esta ação de envio? As mensagens pendentes serão encerradas.')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/campaigns/${campaignId}/cancel`, {
        method: 'PATCH'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Ação de envio cancelada definitivamente');
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao cancelar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao cancelar campanha');
    }
  };

  const loadCampaignReport = async (campaignId: string) => {
    const response = await authenticatedFetch(`/campaigns/${campaignId}/report`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Erro ao carregar relatório');
    }

    return response.json();
  };

  const resetReportModalState = useCallback(() => {
    setShowReportModal(false);
    setCurrentReportCampaignId(null);
    setReportData(null);
    setReportLoading(false);
    setReportCurrentPage(1);
  }, []);

  const handleViewReport = async (campaignId: string) => {
    setCurrentReportCampaignId(campaignId);
    setReportCurrentPage(1);
    setReportData(null);
    setReportLoading(true);
    setHideCertificationNoticeForever(false);
    setShowReportModal(true);

    try {
      const data = await loadCampaignReport(campaignId);
      setReportData(data);
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar relatório');
      resetReportModalState();
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseReportModal = useCallback(() => {
    resetReportModalState();

    const hideNoticePermanently = localStorage.getItem(REPORT_CERTIFICATION_NOTICE_HIDDEN_KEY) === 'true';
    const noticeAlreadySeen = localStorage.getItem(REPORT_CERTIFICATION_NOTICE_SEEN_KEY) === 'true';

    if (!hideNoticePermanently && !noticeAlreadySeen) {
      localStorage.setItem(REPORT_CERTIFICATION_NOTICE_SEEN_KEY, 'true');
      setHideCertificationNoticeForever(false);
      setShowCertificationNotice(true);
    }
  }, [resetReportModalState]);

  const handleDismissCertificationNotice = useCallback(() => {
    if (hideCertificationNoticeForever) {
      localStorage.setItem(REPORT_CERTIFICATION_NOTICE_HIDDEN_KEY, 'true');
    }

    setShowCertificationNotice(false);
  }, [hideCertificationNoticeForever]);

  const handleDismissOfficialIdentificationNotice = useCallback(() => {
    if (hideOfficialIdentificationNoticeForever) {
      localStorage.setItem(OFFICIAL_IDENTIFICATION_NOTICE_HIDDEN_KEY, 'true');
    }

    setShowOfficialIdentificationNotice(false);
  }, [hideOfficialIdentificationNoticeForever]);

  const handleCloseCreateModal = useCallback(() => {
    setShowOfficialIdentificationNotice(false);
    setShowCreateModal(false);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-100 text-gray-800';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Aguardando';
      case 'RUNNING':
        return 'Executando';
      case 'COMPLETED':
        return 'Concluída';
      case 'PAUSED':
        return 'Pausada';
      case 'FAILED':
        return 'Falha';
      case 'CANCELLED':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const handleRefreshReport = useCallback(async () => {
    if (!currentReportCampaignId) return;

    setReportLoading(true);
    try {
      const data = await loadCampaignReport(currentReportCampaignId);
      setReportData(data);
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      if (error instanceof Error && error.message === 'Campanha não encontrada') {
        toast.error('Esta lista nao existe mais.');
        resetReportModalState();
        loadCampaigns();
        return;
      }

      toast.error('Erro ao carregar relatório');
    } finally {
      setReportLoading(false);
    }
  }, [currentReportCampaignId, loadCampaigns, resetReportModalState]);

  useEffect(() => {
    if (!showReportModal || !currentReportCampaignId) {
      return;
    }

    const status = reportData?.campaign?.status;
    const shouldPoll = !status || status === 'RUNNING' || status === 'PENDING';

    if (!shouldPoll) {
      return;
    }

    const interval = window.setInterval(() => {
      if (!reportLoading) {
        handleRefreshReport();
      }
    }, campaignReportPollInterval);

    return () => {
      window.clearInterval(interval);
    };
  }, [showReportModal, currentReportCampaignId, reportData?.campaign?.status, reportLoading, handleRefreshReport, campaignReportPollInterval]);

  const handleDownloadReport = async () => {
    if (!currentReportCampaignId || !reportData) return;

    try {
      const response = await authenticatedFetch(`/campaigns/${currentReportCampaignId}/report/download`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        const safeName = reportData.campaign.nome.replace(/[^a-zA-Z0-9]/g, '_');
        a.download = `relatorio_campanha_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Relatório baixado com sucesso!');
      } else {
        if (response.status === 404) {
          const campaign = reportData.campaign;
          const stats = reportData.stats || {};
          const messages = campaign.messages || [];

          const csvRows: string[][] = [
            ['Relatório da Campanha'],
            ['Nome', campaign.nome],
            ['Status', getStatusText(campaign.status)],
            ['Criado por', campaign.createdByName || 'N/A'],
            ['Data de Criação', new Date(campaign.criadoEm).toLocaleString('pt-BR')],
            ['Data de Início', campaign.startedAt ? new Date(campaign.startedAt).toLocaleString('pt-BR') : 'N/A'],
            ['Data de Conclusão', campaign.completedAt ? new Date(campaign.completedAt).toLocaleString('pt-BR') : 'N/A'],
            [''],
            ['Estatísticas'],
            ['Total de Contatos', stats.total ?? 'N/A'],
            ['Mensagens Enviadas', stats.sent ?? 'N/A'],
            ['Mensagens Falharam', stats.failed ?? 'N/A'],
            ['Mensagens Pendentes', stats.pending ?? 'N/A'],
            [''],
            ['Detalhes das Mensagens'],
            ['Nome', 'Telefone', 'Status', 'Sessão Utilizada', 'Data de Envio', 'Erro']
          ];

          messages.forEach((message: any) => {
            csvRows.push([
              message.contactName,
              message.contactPhone,
              message.status,
              message.sessionName || 'N/A',
              message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A',
              message.errorMessage || 'N/A'
            ]);
          });

          const csvContent = csvRows.map((row) =>
            row.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(',')
          ).join('\n');

          const fallbackBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const fallbackUrl = window.URL.createObjectURL(fallbackBlob);
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = fallbackUrl;
          const safeName = campaign.nome.replace(/[^a-zA-Z0-9]/g, '_');
          link.download = `relatorio_campanha_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fallbackUrl);
          document.body.removeChild(link);
          toast.success('Relatório baixado localmente (endpoint indisponível).');
          return;
        }

        let errorMessage = 'Erro ao baixar relatório';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          const text = await response.text().catch(() => '');
          if (text) {
            errorMessage = text;
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório');
    }
  };

  const getErrorIcon = (errorMessage: string) => {
    if (!errorMessage || errorMessage === 'N/A') return null;

    const error = errorMessage.toLowerCase();

    if (error.includes('network') || error.includes('connection') || error.includes('timeout')) {
      return {
        icon: '🌐',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        category: 'Conectividade'
      };
    }

    if (error.includes('blocked') || error.includes('banned') || error.includes('spam')) {
      return {
        icon: '🚫',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        category: 'Bloqueio'
      };
    }

    if (error.includes('rate limit') || error.includes('quota') || error.includes('limit')) {
      return {
        icon: '⏰',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        category: 'Rate Limit'
      };
    }

    if (error.includes('invalid') || error.includes('not found') || error.includes('number')) {
      return {
        icon: '📱',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        category: 'Número Inválido'
      };
    }

    if (error.includes('permission') || error.includes('unauthorized') || error.includes('forbidden')) {
      return {
        icon: '🔒',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        category: 'Permissão'
      };
    }

    if (error.includes('session') || error.includes('disconnected') || error.includes('logout')) {
      return {
        icon: '📲',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100',
        category: 'Sessão'
      };
    }

    if (error.includes('media') || error.includes('file') || error.includes('download')) {
      return {
        icon: '📁',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        category: 'Mídia'
      };
    }

    // Erro genérico
    return {
      icon: '⚠️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      category: 'Erro Geral'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando ações de envio...</span>
      </div>
    );
  }

  return (
    <>
      {!embedded && (
      <Header
        title="Ações de Envio"
        subtitle={`${campaigns.length} envios ativos`}
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            + Novo envio
          </button>
        }
      />
      )}

      <div className={`${embedded ? 'p-0' : 'p-6'} space-y-6`}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMainTab('acoes')}
              className={`px-4 py-2 rounded text-sm font-medium ${
                mainTab === 'acoes' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ações de envio
            </button>
            <button
              type="button"
              onClick={() => setMainTab('textos')}
              className={`px-4 py-2 rounded text-sm font-medium ${
                mainTab === 'textos' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Textos salvos
            </button>
            </div>
            {embedded && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                + Novo envio
              </button>
            )}
          </div>
        </div>

        {mainTab === 'acoes' && (
          <section className="space-y-6">
        {!embedded && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Total</p>
            <p className="mt-1.5 text-2xl font-semibold text-slate-900">{dashboardStats.total}</p>
            <p className="mt-0.5 text-xs text-slate-500">envios cadastrados</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-800/80">Aguardando</p>
            <p className="mt-1.5 text-2xl font-semibold text-amber-900">{dashboardStats.waiting}</p>
            <p className="mt-0.5 text-xs text-amber-800/70">em andamento ou na fila</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-3.5 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800/80">Concluídos</p>
            <p className="mt-1.5 text-2xl font-semibold text-emerald-900">{dashboardStats.completed}</p>
            <p className="mt-0.5 text-xs text-emerald-800/70">finalizados com sucesso</p>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-3.5 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-800/80">Cancelados</p>
            <p className="mt-1.5 text-2xl font-semibold text-rose-900">{dashboardStats.cancelled}</p>
            <p className="mt-0.5 text-xs text-rose-800/70">interrompidos ou arquivados</p>
          </div>
        </div>
        )}

        <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-3.5 shadow-sm sm:p-4">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Ações de envio</h3>
              <p className="text-xs text-slate-500">Cards modernos com status, progresso e ações rápidas.</p>
            </div>
            <p className="text-xs text-slate-500">{filteredCampaigns.length} resultado(s)</p>
          </div>

          {filteredCampaigns.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center text-slate-500">
              Nenhum envio encontrado
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredCampaigns.map((campaign) => {
                const { label, tone } = getDashboardStatus(campaign.status);
                const badgeClass = getDashboardStatusStyles(tone);
                const progressClass = getDashboardProgressClass(tone);
                const totalContacts = Math.max(campaign.totalContacts || 0, 0);
                const sentContacts = Math.min(campaign.sentCount || 0, totalContacts || campaign.sentCount || 0);
                const progressValue = totalContacts > 0 ? Math.min(100, Math.round((sentContacts / totalContacts) * 100)) : 0;
                const dateLabel = campaign.criadoEm
                  ? new Date(campaign.criadoEm).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })
                  : 'Data indisponível';
                const secondaryAction =
                  campaign.status === 'CANCELLED'
                    ? { label: 'Reenviar', onClick: () => handleReenviarCampaign(campaign.id) }
                    : { label: 'Continuar envio', onClick: () => handleViewReport(campaign.id) };

                return (
                  <article
                    key={campaign.id}
                    className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-sm"
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="min-w-0 text-sm font-semibold text-slate-900">{campaign.nome}</h4>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badgeClass}`}>
                            {label}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span>{dateLabel}</span>
                          <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" />
                          <span>{campaign.totalContacts} contatos</span>
                          <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" />
                          <span>{sentContacts} enviados</span>
                          {isSuperAdmin && campaign.createdByName && (
                            <>
                              <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" />
                              <span>{campaign.createdByName}</span>
                            </>
                          )}
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-slate-600">Progresso</span>
                            <span className="text-slate-500">{sentContacts}/{campaign.totalContacts}</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full ${progressClass} opacity-80 transition-all duration-700 ease-out`}
                              style={{ width: `${progressValue}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-500">{campaign.totalContacts} contatos • {sentContacts} enviados</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 lg:min-w-[170px] lg:justify-end">
                        <button
                          type="button"
                          onClick={() => handleOpenContactsList(campaign.id)}
                          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition active:scale-[0.98] hover:bg-slate-800"
                        >
                          Ver detalhes
                        </button>
                        <button
                          type="button"
                          onClick={secondaryAction.onClick}
                          className={`inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition active:scale-[0.98] ${
                            campaign.status === 'CANCELLED'
                              ? 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                              : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100'
                          }`}
                        >
                          {secondaryAction.label}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition active:scale-[0.98] hover:bg-rose-50"
                          title="Excluir lista"
                        >
                          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M8 2a1 1 0 00-.894.553L6.382 4H4a1 1 0 100 2h.293l.853 9.647A2 2 0 007.133 18h5.734a2 2 0 001.987-1.353L15.707 6H16a1 1 0 100-2h-2.382l-.724-1.447A1 1 0 0012 2H8zm1 6a1 1 0 112 0v5a1 1 0 11-2 0V8zM8 8a1 1 0 00-1 1v5a1 1 0 102 0V9a1 1 0 00-1-1zm5 0a1 1 0 00-1 1v5a1 1 0 102 0V9a1 1 0 00-1-1z" />
                          </svg>
                          <span>Excluir lista</span>
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
          </section>
        )}

        {mainTab === 'acoes' && (
        <div className="hidden">
        <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-900/80">
          Listas finalizadas serão excluídas automaticamente após 30 dias.
        </div>

        <div className="rounded-xl border border-blue-100/80 bg-blue-50/80 px-4 py-2.5 text-[10px] text-blue-900/70">
          <p className="text-[10px] font-normal uppercase tracking-wider text-blue-800/65">Variaveis disponiveis para mensagens e IA</p>
          <p className="mt-1 leading-5">
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{nome}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{telefone}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{email}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{cpf}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{endereco}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{processo}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{mandado}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{categoria}}'}</code>
            <code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{observacoes}}'}</code>
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Ações de envio</h3>
          </div>

          {campaigns.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhuma campanha encontrada. Crie sua primeira campanha para começar.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
                    {/* Nome e Status */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{campaign.nome}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                        {getStatusText(campaign.status)}
                      </span>
                    </div>

                    {/* Informações essenciais */}
                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Contatos:</span>
                        <span>{campaign.totalContacts}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Enviados:</span>
                        <span>{campaign.sentCount}/{campaign.totalContacts}</span>
                      </div>
                      {isSuperAdmin && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Usuário:</span>
                          <span>{campaign.createdByName || 'Sistema'}</span>
                        </div>
                      )}

                      {/* Sessões ativas (apenas ícones) */}
                      {campaign.sessionNames && campaign.sessionNames.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Sessões:</span>
                          <div className="flex gap-1 flex-wrap">
                            {campaign.sessionNames.slice(0, 3).map((sessionName, index) => (
                              <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                {sessionName.substring(0, 3)}
                              </span>
                            ))}
                            {campaign.sessionNames.length > 3 && (
                              <span className="text-xs text-gray-500">+{campaign.sessionNames.length - 3}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Botões de ação */}
                    <div className="flex gap-1 sm:ml-4 self-end sm:self-auto">
                      <button
                        type="button"
                        onClick={() => handleViewReport(campaign.id)}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        title="Ver relatório da campanha"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 3a1 1 0 00-1 1v12a1 1 0 001 1h10a1 1 0 100-2H6V4a1 1 0 00-1-1z" />
                          <path d="M9 13a1 1 0 001-1V8a1 1 0 10-2 0v4a1 1 0 001 1z" />
                          <path d="M12 13a1 1 0 001-1V6a1 1 0 10-2 0v6a1 1 0 001 1z" />
                          <path d="M15 13a1 1 0 001-1V9a1 1 0 10-2 0v3a1 1 0 001 1z" />
                        </svg>
                      </button>
                      {campaign.status === 'RUNNING' && (
                        <button
                          onClick={() => handleToggleCampaign(campaign.id, 'pause')}
                          className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                          title="Pausar campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {campaign.status === 'PAUSED' && (
                        <button
                          onClick={() => handleToggleCampaign(campaign.id, 'resume')}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          title="Retomar campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {['PENDING', 'RUNNING', 'PAUSED'].includes(campaign.status) && (
                        <button
                          onClick={() => handleCancelCampaign(campaign.id)}
                          className="px-2 py-1 bg-red-700 text-white text-xs rounded hover:bg-red-800"
                          title="Cancelar definitivamente a campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.293a1 1 0 00-1.414-1.414L10 8.586 7.707 6.293a1 1 0 00-1.414 1.414L8.586 10l-2.293 2.293a1 1 0 101.414 1.414L10 11.414l2.293 2.293a1 1 0 001.414-1.414L11.414 10l2.293-2.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {['COMPLETED', 'FAILED', 'CANCELLED'].includes(campaign.status) && (
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="inline-flex items-center gap-1.5 rounded border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                          title="Excluir lista"
                        >
                          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span>Excluir lista</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
        )}

        {mainTab === 'textos' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Textos salvos</h2>
              <button
                type="button"
                onClick={() => {
                  setEditingSavedTextId(null);
                  setSavedTextTitle('');
                  setSavedTextBody('');
                  setShowSavedTextModal(true);
                }}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm"
              >
                + Adicionar texto
              </button>
            </div>

            {savedTexts.length === 0 ? (
              <p className="text-gray-500">Nenhum texto salvo.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedSavedTexts.map((template) => (
                  <div key={template.id} className="border rounded p-4 flex flex-col justify-between">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-800">{template.title}</h3>
                      <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap" style={{ maxHeight: '6rem', overflow: 'hidden' }}>
                        {template.body}
                      </p>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditSavedText(template)}
                        className="text-blue-600 text-sm"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Apagar este texto?')) {
                            setSavedTexts((prev) => prev.filter((item) => item.id !== template.id));
                          }
                        }}
                        className="text-red-600 text-sm"
                      >
                        Apagar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showSavedTextModal && (
              <div className="fixed inset-0 bg-gray-800 bg-opacity-40 flex items-center justify-center z-40">
                <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg">
                  <h3 className="text-xl font-semibold mb-4">
                    {editingSavedTextId ? 'Editar texto' : 'Novo texto'}
                  </h3>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">Título</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={savedTextTitle}
                      onChange={(e) => setSavedTextTitle(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">Texto</label>
                    <textarea
                      rows={8}
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={savedTextBody}
                      onChange={(e) => setSavedTextBody(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleCancelEditSavedText}
                      className="px-3 py-1 bg-gray-300 rounded text-sm"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateSavedText}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal de Criação */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-30 p-4 sm:p-6 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Nova Ação de envio</h3>
                  <p className="text-sm text-gray-600 mt-1">Configure sua ação de envio de mensagens</p>
                </div>
                <button
                  onClick={handleCloseCreateModal}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateCampaign} className="p-4 sm:p-6">
                <OfficialIdentificationCard
                  title="Identificação do Oficial"
                  description="Envie aqui a imagem exclusiva que será anexada nas mensagens desta ação de envio, sem se confundir com o documento usado no cadastro do usuário."
                  className="mb-6 border-blue-100 bg-blue-50/40"
                  onChange={setOfficialIdentification}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                  {/* COLUNA ESQUERDA - Informações Básicas */}
                  <div className="space-y-6">
                    {/* <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="text-lg font-semibold text-blue-900 mb-2">Informações Básicas</h4>
                      <p className="text-sm text-blue-700">Configure os dados fundamentais da sua campanha</p>
                    </div> */}

                    {/* Nome da Campanha */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Campanha *
                      </label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Mandados do mês de Junho"
                        required
                      />
                    </div>

                    {/* Categorias de Contatos */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categorias de Contatos *
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Selecione quais categorias de contatos receberão a campanha</p>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {contactTags.map((tag) => (
                          <label key={tag.id} className="flex items-center space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.targetTags.includes(tag.id)}
                              onChange={() => handleTagToggle(tag.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 font-medium">{tag.nome}</span>
                          </label>
                        ))}
                        {contactTags.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria disponível</p>
                        )}
                      </div>
                      {formData.targetTags.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.targetTags.length} categoria(s) selecionada(s)</p>
                      )}
                    </div>

                    {/* Conexões WhatsApp */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conexões WhatsApp * ({formData.sessionNames.length} selecionadas)
                      </label>
                      {/* <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-green-800">Multi-Sessão com Failover</h4>
                            <p className="text-sm text-green-700 mt-1">
                              Sistema inteligente que distribui envios entre múltiplas conexões com redundância automática.
                            </p>
                          </div>
                        </div>
                      </div> */}
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {whatsappSessions.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                        ) : (
                          whatsappSessions.map((session) => (
                            <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.sessionNames.includes(session.name)}
                                  onChange={() => handleSessionToggle(session.name)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {session.mePushName || session.displayName || session.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">({session.displayName || session.name})</span>
                                </div>
                              </div>
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                Ativa
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      {formData.sessionNames.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.sessionNames.length} conexão(ões) selecionada(s)</p>
                      )}
                    </div>

                    {/* Configuração de Envio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tempo entre envios (segundos)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="300"
                        value={formData.randomDelay}
                        onChange={(e) => {
                          const parsedValue = Number.parseInt(e.target.value, 10);
                          setFormData(prev => ({
                            ...prev,
                            randomDelay: Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue)
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[10, 20, 30, 60].map((seconds) => (
                          <button
                            key={seconds}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, randomDelay: seconds }))}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            {seconds}s
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        ⏱️ O sistema aplica um intervalo aleatório de 0 até {formData.randomDelay}s entre envios.
                      </p>
                    </div>

                    {/* Data de Envio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quando Enviar
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">🚀 Iniciar imediatamente</span>
                            <p className="text-xs text-gray-500">A campanha será executada assim que for criada</p>
                          </div>
                        </label>
                        <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            checked={!formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">📅 Agendar para:</span>
                            <p className="text-xs text-gray-500">Escolha data e hora específicas</p>
                          </div>
                        </label>
                        {!formData.startImmediately && (
                          <input
                            type="datetime-local"
                            value={formData.scheduledFor}
                            onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ml-6"
                            required={!formData.startImmediately}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* COLUNA DIREITA - Mensagens */}
                  <div className="space-y-6">
                    {/* <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="text-lg font-semibold text-purple-900 mb-2">💬 Mensagens da Campanha</h4>
                      <p className="text-sm text-purple-700">Configure o conteúdo que será enviado aos contatos</p>
                    </div> */}

                    <div>
                      {/* <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-blue-800">💡 Variáveis Dinâmicas</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Personalize suas mensagens com:
                              <span className="block mt-1">
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{nome}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{cpf}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{telefone}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{email}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{categoria}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{observacoes}}'}</code>
                              </span>
                            </p>
                          </div>
                        </div>
                      </div> */}

                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-600">Adicione uma ou mais mensagens para serem enviadas em sequência</p>
                          <button
                            type="button"
                            onClick={() => {
                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                              const newSequence = [...currentSequence, { type: 'text', content: { text: '' } }];
                              setFormData(prev => ({
                                ...prev,
                                messageContent: { sequence: newSequence }
                              }));
                            }}
                            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-2"
                          >
                            <span className="text-lg">+</span>
                            Mensagem
                          </button>
                        </div>

                        <div className="space-y-3">
                          {('sequence' in formData.messageContent) && formData.messageContent.sequence.map((item, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-medium text-gray-600">Mensagem {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                    const newSequence = currentSequence.filter((_, i) => i !== index);
                                    setFormData(prev => ({
                                      ...prev,
                                      messageContent: { sequence: newSequence }
                                    }));
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remover
                                </button>
                              </div>

                              <div className="space-y-3">
                                <select
                                  value={item.type}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    let newContent;
                                    switch (newType) {
                                      case 'text':
                                        newContent = { text: '' };
                                        break;
                                      case 'document':
                                        newContent = { url: '', fileName: '' };
                                        break;
                                      case 'openai':
                                        newContent = { model: '', system: '', user: '' };
                                        break;
                                      case 'groq':
                                        newContent = { model: '', system: '', user: '' };
                                        break;
                                      default:
                                        newContent = { url: '', caption: '' };
                                        break;
                                    }

                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                    const newSequence = currentSequence.map((seqItem, i) =>
                                      i === index ? { type: newType, content: newContent } : seqItem
                                    );
                                    setFormData(prev => ({
                                      ...prev,
                                      messageContent: { sequence: newSequence }
                                    }));
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="text">Texto</option>
                                  <option value="image">Imagem</option>
                                  <option value="video">Vídeo</option>
                                  <option value="audio">Áudio</option>
                                  <option value="document">Arquivo</option>
                                  <option value="openai">OpenAI</option>
                                  <option value="groq">Groq AI</option>
                                </select>

                                {item.type === 'text' && (
                                  <div className="space-y-2">
                                    {/* Checkbox para usar variações */}
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        id={`useVariations-${index}`}
                                        checked={item.content.useVariations || false}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: {
                                                ...seqItem.content,
                                                useVariations: e.target.checked,
                                                variations: e.target.checked ? [''] : undefined,
                                                text: e.target.checked ? undefined : seqItem.content.text
                                              }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                      />
                                      <label htmlFor={`useVariations-${index}`} className="text-sm font-medium text-gray-700">
                                        Usar variações de texto
                                      </label>
                                    </div>

                                    {item.content.useVariations ? (
                                      /* Modo variações */
                                      <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                          Variações de texto
                                        </label>
                                        {(item.content.variations || ['']).map((variation: string, varIndex: number) => (
                                          <div key={varIndex} className="flex gap-2">
                                            <div className="flex-1">
                                              <textarea
                                                placeholder={`Variação ${varIndex + 1}... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}`}
                                                value={variation}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations[varIndex] = e.target.value;
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                rows={2}
                                              />
                                            </div>
                                            {(item.content.variations || []).length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations.splice(varIndex, 1);
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="px-2 py-1 text-red-600 hover:text-red-800"
                                                title="Remover variação"
                                              >
                                                ✕
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                const newVariations = [...(seqItem.content.variations || []), ''];
                                                return {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, variations: newVariations }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200 flex items-center gap-1"
                                        >
                                          <span>+</span>
                                          Nova variação
                                        </button>
                                      </div>
                                    ) : (
                                      /* Modo texto único */
                                      <div>
                                        <div className="mb-2 flex flex-wrap items-center gap-2">
                                          <select
                                            value={selectedSavedTextByMessage[index] || ''}
                                            onChange={(e) => {
                                              const templateId = e.target.value;
                                              setSelectedSavedTextByMessage((prev) => ({
                                                ...prev,
                                                [index]: templateId
                                              }));
                                            }}
                                            className="px-2 py-1 border border-gray-300 rounded text-xs text-gray-700"
                                          >
                                            <option value="">Selecionar texto salvo...</option>
                                            {sortedSavedTexts.map((template) => (
                                              <option key={template.id} value={template.id}>
                                                {template.title}
                                              </option>
                                            ))}
                                          </select>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const selectedId = selectedSavedTextByMessage[index];
                                              const template = savedTexts.find((item) => item.id === selectedId);
                                              if (!template) {
                                                toast.error('Selecione um texto salvo');
                                                return;
                                              }
                                              applySavedTextToMessage(template, index);
                                            }}
                                            className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                                            disabled={savedTexts.length === 0}
                                          >
                                            Inserir texto salvo
                                          </button>
                                        </div>
                                        <textarea
                                          placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                          value={item.content.text || ''}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? { ...seqItem, content: { text: e.target.value } } : seqItem
                                            );
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          rows={3}
                                        />
                                        <div className="mt-2 flex justify-end">
                                          <button
                                            type="button"
                                            onClick={() => saveTextTemplate(`Mensagem ${index + 1}`, item.content.text || '')}
                                            className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                                          >
                                            Salvar texto
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    <div className="flex flex-wrap gap-1">
                                      <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                      {['{{nome}}', '{{email}}', '{{cpf}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                        <button
                                          key={variable}
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            if (item.content.useVariations) {
                                              // Adicionar à primeira variação vazia ou criar nova
                                              const variations = item.content.variations || [''];
                                              let targetIndex = variations.findIndex((v: string) => !v.trim());
                                              if (targetIndex === -1) targetIndex = 0;

                                              const newSequence = currentSequence.map((seqItem, i) => {
                                                if (i === index) {
                                                  const newVariations = [...variations];
                                                  newVariations[targetIndex] = (newVariations[targetIndex] || '') + variable;
                                                  return {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, variations: newVariations }
                                                  };
                                                }
                                                return seqItem;
                                              });
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            } else {
                                              // Adicionar ao texto único
                                              const currentText = currentSequence[index]?.content?.text || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { text: currentText + variable } } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }
                                          }}
                                          className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                        >
                                          {variable}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {item.type === 'openai' && (
                                  <div className="space-y-4">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-blue-600 font-medium">🤖 OpenAI</span>
                                      </div>
                                      <p className="text-sm text-blue-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando inteligência artificial
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modelo *
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Digite o modelo da OpenAI (ex: gpt-3.5-turbo)"
                                        value={item.content.model || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, model: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'].map((model) => (
                                            <button
                                              key={model}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, model: model }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 border border-gray-300"
                                            >
                                              {model}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{email}}', '{{cpf}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {item.type === 'groq' && (
                                  <div className="space-y-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-yellow-600 font-medium">⚡ Groq AI</span>
                                      </div>
                                      <p className="text-sm text-yellow-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando Groq AI com velocidade ultra-rápida
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modelo *
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Digite o modelo do Groq (ex: llama-3.1-8b-instant)"
                                        value={item.content.model || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, model: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {['llama-3.1-8b-instant', 'llama-3.1-70b-versatile', 'llama-3.2-11b-text-preview', 'mixtral-8x7b-32768', 'gemma2-9b-it'].map((model) => (
                                            <button
                                              key={model}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, model: model }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded hover:bg-yellow-200 border border-yellow-300"
                                            >
                                              {model}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{email}}', '{{cpf}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded hover:bg-orange-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {['image', 'video', 'audio', 'document'].includes(item.type) && (
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <label className="block text-sm font-medium text-gray-700">
                                        Arquivo
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={item.content.useMediaVariations || false}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                return {
                                                  ...seqItem,
                                                  content: {
                                                    ...seqItem.content,
                                                    useMediaVariations: e.target.checked,
                                                    mediaVariations: e.target.checked ? Array.from({ length: 4 }, () => ({ url: '', caption: '', fileName: '' })) : undefined,
                                                    // Limpar URL principal quando ativar variações
                                                    url: e.target.checked ? '' : seqItem.content.url
                                                  }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-600">Usar Variações</span>
                                      </label>
                                    </div>

                                    {item.content.useMediaVariations ? (
                                      // Grid horizontal de 4 variações
                                      <div className="grid grid-cols-4 gap-3">
                                        {Array.from({ length: 4 }, (_, varIndex) => {
                                          const mediaVariations = item.content.mediaVariations || [];
                                          const variation = mediaVariations[varIndex] || { url: '', caption: '', fileName: '' };
                                          const hasFile = variation.url;

                                          return (
                                            <div key={varIndex} className="space-y-2">
                                              {/* Header da variação */}
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium text-gray-600">
                                                  {varIndex === 0 ? 'Principal' : `Var. ${varIndex + 1}`}
                                                </span>
                                                {hasFile && varIndex > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                      const newSequence = currentSequence.map((seqItem, i) => {
                                                        if (i === index) {
                                                          const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                          newVariations[varIndex] = { url: '', caption: '', fileName: '' };
                                                          return {
                                                            ...seqItem,
                                                            content: { ...seqItem.content, mediaVariations: newVariations }
                                                          };
                                                        }
                                                        return seqItem;
                                                      });
                                                      setFormData(prev => ({
                                                        ...prev,
                                                        messageContent: { sequence: newSequence }
                                                      }));
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-xs p-0.5"
                                                    title="Remover"
                                                  >
                                                    ✕
                                                  </button>
                                                )}
                                              </div>

                                              {/* Preview quadrado */}
                                              <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                                                {!hasFile ? (
                                                  <>
                                                    <input
                                                      type="file"
                                                      id={`file-upload-${index}-${varIndex}`}
                                                      className="hidden"
                                                      accept={
                                                        item.type === 'image' ? 'image/*' :
                                                        item.type === 'video' ? 'video/*' :
                                                        item.type === 'audio' ? 'audio/*' :
                                                        'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                                      }
                                                      onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                          handleFileUpload(file, index, varIndex);
                                                        }
                                                      }}
                                                      disabled={uploadingFiles[`${index}-${varIndex}`]}
                                                    />
                                                    <label
                                                      htmlFor={`file-upload-${index}-${varIndex}`}
                                                      className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                        uploadingFiles[`${index}-${varIndex}`]
                                                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                                          : 'text-gray-500 hover:text-blue-600'
                                                      }`}
                                                    >
                                                      {uploadingFiles[`${index}-${varIndex}`] ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                          <span className="text-xs">Enviando</span>
                                                        </div>
                                                      ) : (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="text-2xl">
                                                            {item.type === 'image' && '🖼️'}
                                                            {item.type === 'video' && '🎥'}
                                                            {item.type === 'audio' && '🎵'}
                                                            {item.type === 'document' && '📄'}
                                                          </div>
                                                          <span className="text-xs text-center">
                                                            Fazer upload
                                                          </span>
                                                        </div>
                                                      )}
                                                    </label>
                                                  </>
                                                ) : (
                                                  <div className="w-full h-full relative group">
                                                    {item.type === 'image' && (
                                                      <img
                                                        src={variation.url}
                                                        alt={`Variação ${varIndex + 1}`}
                                                        className="w-full h-full object-cover"
                                                      />
                                                    )}

                                                    {item.type === 'video' && (
                                                      <div className="w-full h-full relative">
                                                        <video
                                                          src={variation.url}
                                                          className="w-full h-full object-cover"
                                                          muted
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                                          <div className="bg-white bg-opacity-90 p-1 rounded-full">
                                                            <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                                                              <path d="M8 5v14l11-7z"/>
                                                            </svg>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    )}

                                                    {item.type === 'audio' && (
                                                      <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 flex flex-col items-center justify-center">
                                                        <div className="text-3xl mb-1">🎵</div>
                                                        <div className="text-xs text-center text-gray-700 px-1">
                                                          Áudio
                                                        </div>
                                                      </div>
                                                    )}

                                                    {item.type === 'document' && (
                                                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex flex-col items-center justify-center p-2">
                                                        <div className="text-3xl mb-1">📄</div>
                                                        <div className="text-xs text-center text-gray-700 truncate w-full">
                                                          {variation.fileName || 'Documento'}
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* Overlay de ações */}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                      <a
                                                        href={variation.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bg-white bg-opacity-90 px-2 py-1 rounded text-xs font-medium"
                                                      >
                                                        Ver
                                                      </a>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>

                                              {!hasFile && item.type === 'image' && (
                                                <button
                                                  type="button"
                                                  onClick={() => applyOfficialIdentificationToMessage(index, varIndex)}
                                                  className="w-full px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded hover:bg-amber-200"
                                                >
                                                  Usar identificação do oficial
                                                </button>
                                              )}

                                              {/* Campo de legenda para imagem e vídeo */}
                                              {hasFile && ['image', 'video'].includes(item.type) && (
                                                <div className="space-y-2">
                                                  <input
                                                    type="text"
                                                    placeholder="Legenda..."
                                                    value={variation.caption || ''}
                                                    onChange={(e) => {
                                                      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                      const newSequence = currentSequence.map((seqItem, i) => {
                                                        if (i === index) {
                                                          const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                          // Garantir que o array tenha o tamanho necessário
                                                          while (newVariations.length <= varIndex) {
                                                            newVariations.push({ url: '', caption: '', fileName: '' });
                                                          }
                                                          newVariations[varIndex] = { ...newVariations[varIndex], caption: e.target.value };
                                                          return {
                                                            ...seqItem,
                                                            content: { ...seqItem.content, mediaVariations: newVariations }
                                                          };
                                                        }
                                                        return seqItem;
                                                      });
                                                      setFormData(prev => ({
                                                        ...prev,
                                                        messageContent: { sequence: newSequence }
                                                      }));
                                                    }}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                  />
                                                  {['image', 'video'].includes(item.type) && variation.url && (
                                                    <div className="flex flex-wrap gap-2">
                                                      <a
                                                        href={variation.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                                      >
                                                        Abrir mídia
                                                      </a>
                                                      <button
                                                        type="button"
                                                        onClick={() => copyTextToClipboard(variation.url, 'Link da mídia copiado')}
                                                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                                                      >
                                                        Copiar link
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => appendMediaLinkToCaption(index, variation.url, varIndex)}
                                                        className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded hover:bg-emerald-200"
                                                      >
                                                        Inserir link na legenda
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                              )}

                                              {/* Nome do arquivo para não-imagens */}
                                              {hasFile && !['image', 'video'].includes(item.type) && (
                                                <div className="text-xs text-gray-600 truncate" title={variation.fileName}>
                                                  {variation.fileName || 'Arquivo'}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      // Interface original de arquivo único
                                      <>
                                        {!item.content.url ? (
                                          <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                              <input
                                                type="file"
                                                id={`file-upload-${index}`}
                                                className="hidden"
                                                accept={
                                                  item.type === 'image' ? 'image/*' :
                                                  item.type === 'video' ? 'video/*' :
                                                  item.type === 'audio' ? 'audio/*' :
                                                  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                                }
                                                onChange={(e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file) {
                                                    handleFileUpload(file, index);
                                                  }
                                                }}
                                                disabled={uploadingFiles[index]}
                                              />
                                              <label
                                                htmlFor={`file-upload-${index}`}
                                                className={`flex-1 cursor-pointer px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                  uploadingFiles[index]
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                                    : 'bg-white text-gray-700'
                                                }`}
                                              >
                                                {uploadingFiles[index] ? (
                                                  <div className="flex flex-col items-center gap-2">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                    <span>Enviando arquivo...</span>
                                                  </div>
                                                ) : (
                                                  <div className="flex flex-col items-center gap-2">
                                                    <div className="text-2xl">📁</div>
                                                    <span>Clique para fazer upload do arquivo</span>
                                                    <span className="text-xs text-gray-500">
                                                      {item.type === 'image' && 'Imagens: JPG, PNG, GIF, WebP'}
                                                      {item.type === 'video' && 'Vídeos: MP4, AVI, MOV, WMV, MKV'}
                                                      {item.type === 'audio' && 'Áudios: MP3, WAV, OGG, AAC, M4A'}
                                                      {item.type === 'document' && 'Documentos: PDF, DOC, XLS, TXT, ZIP'}
                                                    </span>
                                                  </div>
                                                )}
                                              </label>
                                            </div>
                                            {item.type === 'image' && (
                                              <button
                                                type="button"
                                                onClick={() => applyOfficialIdentificationToMessage(index)}
                                                className="w-full px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-200"
                                              >
                                                Usar identificação do oficial
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-3">
                                                {item.type === 'image' && (
                                                  <img
                                                    src={item.content.url}
                                                    alt="Preview"
                                                    className="w-12 h-12 object-cover rounded"
                                                  />
                                                )}
                                                {item.type === 'video' && (
                                                  <video
                                                    src={item.content.url}
                                                    className="w-12 h-12 object-cover rounded"
                                                    muted
                                                  />
                                                )}
                                                {!['image', 'video'].includes(item.type) && (
                                                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-2xl">
                                                    {getFileIcon(fileInfos[index]?.type || item.type)}
                                                  </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-sm font-medium text-gray-900 truncate">
                                                    {fileInfos[index]?.name || 'Arquivo carregado'}
                                                  </div>
                                                  <div className="text-xs text-gray-500">
                                                    {fileInfos[index]?.size ? formatFileSize(fileInfos[index].size) : ''}
                                                  </div>
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Remover arquivo"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                              </button>
                                            </div>
                                            {['image', 'video'].includes(item.type) && item.content.url && (
                                              <div className="mt-3 flex flex-wrap gap-2">
                                                <a
                                                  href={item.content.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                                >
                                                  Abrir mídia
                                                </a>
                                                <button
                                                  type="button"
                                                  onClick={() => copyTextToClipboard(item.content.url, 'Link da mídia copiado')}
                                                  className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                                                >
                                                  Copiar link
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => appendMediaLinkToCaption(index, item.content.url)}
                                                  className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs rounded hover:bg-emerald-200"
                                                >
                                                  Inserir link na legenda
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {['image', 'video'].includes(item.type) && !item.content.useMediaVariations && (
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          placeholder="Legenda (opcional) - Use variáveis como {{nome}}, {{telefone}}, etc."
                                          value={item.content.caption || ''}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? {
                                                ...seqItem,
                                                content: { ...seqItem.content, caption: e.target.value }
                                              } : seqItem
                                            );
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="flex flex-wrap gap-1">
                                          <span className="text-xs text-gray-500">Variáveis:</span>
                                          {['{{nome}}', '{{telefone}}', '{{categoria}}'].map((variable) => (
                                            <button
                                              key={variable}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const currentCaption = currentSequence[index]?.content?.caption || '';
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, caption: currentCaption + variable }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                            >
                                              {variable}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {item.type === 'document' && !item.content.useMediaVariations && (
                                      <input
                                        type="text"
                                        placeholder="Nome do arquivo (opcional)"
                                        value={item.content.fileName || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, fileName: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {(!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) && (
                            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                              <div className="text-4xl mb-4">📝</div>
                              <p className="text-lg font-medium">Nenhuma mensagem na sequência</p>
                              <p className="text-sm">Clique em "Adicionar Mensagem" para começar a criar sua campanha.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseCreateModal}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingCampaign}
                    className={`w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-all font-medium shadow-lg ${
                      creatingCampaign ? 'opacity-70 cursor-not-allowed' : 'hover:from-blue-700 hover:to-purple-700'
                    }`}
                  >
                    {creatingCampaign ? 'Criando...' : 'Criar Campanha'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Relatórios */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-30 p-4 sm:p-6 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] overflow-y-auto relative"
                 style={{ zIndex: 1000 }}>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 sm:p-6 border-b">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <h3 className="text-lg font-medium text-gray-900">Relatório da Campanha</h3>
                  <button
                    onClick={handleRefreshReport}
                    disabled={reportLoading}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Atualizar relatório"
                  >
                    <svg className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {reportLoading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                  {reportLoading && reportData && (
                    <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                      <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
                      Atualizando dados...
                    </span>
                  )}
                </div>
                <button
                  onClick={handleCloseReportModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {reportLoading && !reportData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Carregando relatório...</span>
                  </div>
                ) : reportData ? (
                  <div className="space-y-6">
                    {/* Informações da Campanha */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">{reportData.campaign.nome}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Status:</span>
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reportData.campaign.status)}`}>
                            {getStatusText(reportData.campaign.status)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Criado por:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.createdByName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Criação:</span>
                          <span className="ml-2 text-gray-900">{new Date(reportData.campaign.criadoEm).toLocaleString('pt-BR')}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Início:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.startedAt ? new Date(reportData.campaign.startedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Conclusão:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.completedAt ? new Date(reportData.campaign.completedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Acompanhamento de Envio */}
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">Acompanhamento de envio</h4>
                        <span className="text-xs text-gray-500">
                          {reportData.campaign.status === 'RUNNING' || reportData.campaign.status === 'PENDING'
                            ? 'Atualizando a cada 5s'
                            : 'Atualização manual disponível'}
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <span className="font-medium text-blue-700">Enviadas: {reportSent}</span>
                        <span className="font-medium text-gray-700">Restantes: {reportPending}</span>
                        <span className="font-medium text-red-600">Falhas: {reportFailed}</span>
                      </div>

                      <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${reportProgress}%` }}
                        />
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {reportSent + reportFailed} de {reportTotal} processados
                      </div>
                    </div>

                    {reportPendingMessages.length > 0 && (
                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900">Em envio agora</h4>
                        <ul className="mt-2 space-y-2 text-xs text-gray-600 max-h-32 overflow-y-auto">
                          {reportPendingMessages.slice(0, 6).map((message: any) => (
                            <li key={message.id} className="flex items-center justify-between gap-3">
                              <span className="truncate">{message.contactName}</span>
                              <span className="text-[10px] text-gray-400">
                                {message.sessionName || 'N/A'}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {reportPendingMessages.length > 6 && (
                          <div className="mt-2 text-[11px] text-gray-500">
                            +{reportPendingMessages.length - 6} pendentes
                          </div>
                        )}
                      </div>
                    )}

                    {/* Estatísticas */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Estatísticas de Envio</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-blue-600">{reportData.stats.total}</div>
                          <div className="text-sm text-blue-800">Total de Contatos</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-600">{reportData.stats.sent}</div>
                          <div className="text-sm text-green-800">Mensagens Enviadas</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-red-600">{reportData.stats.failed}</div>
                          <div className="text-sm text-red-800">Mensagens Falharam</div>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-yellow-600">{reportData.stats.pending}</div>
                          <div className="text-sm text-yellow-800">Mensagens Pendentes</div>
                        </div>
                      </div>
                    </div>

                    {/* Mensagens por Sessão */}
                    {Object.keys(reportData.messagesBySession).length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Distribuição por Sessão</h4>
                        <div className="bg-white border rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessão</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enviadas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Falharam</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {Object.entries(reportData.messagesBySession).map(([sessionName, sessionData]) => {
                                const messages = (sessionData as any).messages || [];
                                return (
                                  <tr key={sessionName}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sessionName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{messages.length}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                      {messages.filter((m: any) => m.status === 'SENT').length}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                      {messages.filter((m: any) => m.status === 'FAILED').length}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Mensagens Detalhadas */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">Detalhes das Mensagens</h4>
                        <button
                          onClick={handleDownloadReport}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                        >
                          📊 Download CSV
                        </button>
                      </div>

                      {(() => {
                        const messages = reportData.campaign.messages || [];
                        const totalPages = Math.ceil(messages.length / reportItemsPerPage);
                        const startIndex = (reportCurrentPage - 1) * reportItemsPerPage;
                        const endIndex = startIndex + reportItemsPerPage;
                        const currentMessages = messages.slice(startIndex, endIndex);

                        return (
                          <>
                            <div className="bg-white border rounded-lg overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessão</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data de Envio</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Erro</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {currentMessages.map((message: any) => (
                                    <tr key={message.id}>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.contactName}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.contactPhone}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          message.status === 'SENT' ? 'bg-green-100 text-green-800' :
                                          message.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                          'bg-yellow-100 text-yellow-800'
                                        }`}>
                                          {message.status === 'SENT' ? 'Enviada' :
                                           message.status === 'FAILED' ? 'Falhou' : 'Pendente'}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.sessionName || 'N/A'}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {message.errorMessage && message.errorMessage !== 'N/A' ? (
                                          (() => {
                                            const errorInfo = getErrorIcon(message.errorMessage);
                                            return errorInfo ? (
                                              <div className="group relative inline-flex items-center">
                                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${errorInfo.bgColor} cursor-help`}>
                                                  <span className="text-lg">{errorInfo.icon}</span>
                                                </div>

                                                {/* Tooltip com posicionamento inteligente */}
                                                <div className="group-hover:block hidden absolute z-[9999]">
                                                  {/* Versão à direita */}
                                                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700 group-hover/right:block hidden">
                                                    {/* Seta esquerda */}
                                                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-l border-b border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Versão à esquerda (fallback) */}
                                                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700">
                                                    {/* Seta direita */}
                                                    <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-r border-t border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <span className="text-sm text-red-600">
                                                {message.errorMessage}
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span className="text-sm text-gray-400 flex items-center justify-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                                              <span className="text-lg">✅</span>
                                            </div>
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {messages.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                  Nenhuma mensagem encontrada
                                </div>
                              )}
                            </div>

                            {/* Paginação */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-gray-700">
                                  Mostrando {startIndex + 1} a {Math.min(endIndex, messages.length)} de {messages.length} mensagens
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.max(page - 1, 1))}
                                    disabled={reportCurrentPage === 1}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Anterior
                                  </button>

                                  <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                      <button
                                        key={pageNum}
                                        onClick={() => setReportCurrentPage(pageNum)}
                                        className={`px-3 py-2 text-sm rounded-md ${
                                          pageNum === reportCurrentPage
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    ))}
                                  </div>

                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.min(page + 1, totalPages))}
                                    disabled={reportCurrentPage === totalPages}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Próximo
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Erro ao carregar relatório
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showCertificationNotice && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Aviso</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Os dados desta Ação de Envio serão encaminhados automaticamente para a Área de Certificação. Lá, você poderá realizar a certificação dos seus mandados de forma rápida e prática.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDismissCertificationNotice}
                  className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Fechar aviso"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <label className="mt-5 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={hideCertificationNoticeForever}
                  onChange={(event) => setHideCertificationNoticeForever(event.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Não mostrar essa mensagem novamente</span>
              </label>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleDismissCertificationNotice}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        )}

        {showOfficialIdentificationNotice && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Aviso</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Se quiser usar a identificação do oficial nesta nova Ação de Envio, confira ou anexe novamente a imagem no campo próprio antes de montar a mensagem.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDismissOfficialIdentificationNotice}
                  className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Fechar aviso"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <label className="mt-5 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={hideOfficialIdentificationNoticeForever}
                  onChange={(event) => setHideOfficialIdentificationNoticeForever(event.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Não mostrar essa mensagem novamente</span>
              </label>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleDismissOfficialIdentificationNotice}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
